import { PlayerListItemFragment } from '@amf/shared/types/graphql';
import usePlayerLastMatches, { LIMIT } from '@amf/shared/data/hooks/usePlayerLastMatches';
import { useTranslate } from '@tolgee/react';
import Link from 'next/link';
import { fromUnixTime } from 'date-fns';
import { useRouter } from 'next/router';
import { formatDate, getDateLocale } from '@amf/shared/utils/date';
import Button from '@amf/shared/components/button/Button';

interface PlayerLastMatchesProps {
  player: PlayerListItemFragment;
  prefixes: { match: string };
  title?: string;
  disableAssists?: boolean;
  disablePoints?: boolean;
}

function PlayerLastMatches({
  player,
  prefixes,
  title,
  disableAssists,
  disablePoints,
}: PlayerLastMatchesProps) {
  const { t } = useTranslate();
  const { matches, loading, data, size, setSize } = usePlayerLastMatches(player.id);
  const { locale: appLocale } = useRouter();
  const locale = getDateLocale(appLocale);

  const renderLoadMore = () => {
    if (!matches || data === undefined) {
      return null;
    }

    if (size > data.length) {
      return (
        <Button type='button' variant='dark-outline' disabled>
          {t('team.matches.loading')}
        </Button>
      );
    }

    if (LIMIT * size > matches.length) {
      return null;
    }

    return (
      <Button type='button' variant='dark-outline' onClick={() => setSize(size + 1)}>
        {t('team.matches.more')}
      </Button>
    );
  };

  if (!matches && !loading) {
    return null;
  }

  return (
    <div className='PlayerLastMatches'>
      {title && <h2 className='PlayerLastMatches__title'>{title}</h2>}
      <div className='PlayerLastMatches__content'>
        <table>
          <thead>
            <tr>
              <th className='left'>{t('player.stats.date')}</th>
              <th className='left'>{t('player.stats.league')}</th>
              <th className='left'>{t('player.stats.match')}</th>
              <th>{t('player.stats.goals')}</th>
              {!disableAssists && <th>{t('player.stats.assists')}</th>}
              {!disablePoints && <th>{t('player.stats.points')}</th>}
              <th>{t('player.stats.yellowCards')}</th>
              <th>{t('player.stats.redCards')}</th>
            </tr>
          </thead>
          <tbody>
            {matches &&
              matches.map(match => {
                const date = fromUnixTime(match.match.dateStart);
                return (
                  <tr key={match.id}>
                    <td className='left'>{formatDate(date, 'dd/MM/yy HH:mm', { locale })}</td>
                    <td className='left'>{match.match.leagueName}</td>
                    <td className='left'>
                      <Link href={`${prefixes.match}/${match.match.id}`}>
                        <a>
                          <span>{match.match.home?.team?.name}</span>{' '}
                          <strong>({match.match.score})</strong>{' '}
                          <span>{match.match.visiting?.team?.name}</span>
                          <i className='bi bi-arrow-up-right-circle' />
                        </a>
                      </Link>
                    </td>
                    <td>{match.goals}</td>
                    {!disableAssists && <td>{match.assists}</td>}
                    {!disablePoints && <td>{match.points}</td>}
                    <td>{match.yellowCards}</td>
                    <td>{match.redCards}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        <div className='TeamMatches__pagination'>{renderLoadMore()}</div>
      </div>
    </div>
  );
}

export default PlayerLastMatches;
